<template>
  <div class="unidade-linhas-table d-flex flex-column pa-2">
    <v-alert v-if="!unidade || unidade.id_unidade == null" type="info" dense outlined>
      {{ $t('crudLinhas.detail.selectResource') }}
    </v-alert>
    <template v-else>
      <v-row v-if="canEdit" ref="associarRow" dense align="center" class="flex-grow-0">
        <v-col cols="10">
          <MultiplaEscolha
            ref="linhasAssociarSelect"
            lazy
            get-url="getLinhaslazy"
            text="nome_linha"
            value="id_linha"
            :label="$t('crudUnidades.lines.associate')"
            :max-items="3"
            v-bind:selected-items.sync="linhasParaAssociar"
          />
        </v-col>
        <v-col cols="2" class="text-right">
          <v-btn
            color="primary"
            dark
            small
            fab
            :loading="saving"
            :disabled="saving || !linhasParaAssociar.length"
            :title="$t('crudUnidades.lines.associate')"
            @click="associarLinhas"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-text-field
        v-model="searchLinhas"
        append-icon="mdi-magnify"
        :label="$t('crudLinhas.search')"
        single-line
        hide-details
        dense
        clearable
        class="mb-2 flex-grow-0"
      />

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2 flex-grow-0" />

      <v-list dense class="unidade-linhas-table__list flex-grow-1 py-0">
        <v-list-item
          v-for="item in linhasFiltradas"
          :key="'ul-' + item.id_linha"
          @click="$emit('select-line', item)"
        >
          <v-list-item-content>
            <v-list-item-title>{{ item.nome_linha || item.cod_linha || item.id_linha }}</v-list-item-title>
            <v-list-item-subtitle v-if="item.cod_linha && item.nome_linha">
              {{ item.cod_linha }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="canEdit">
            <v-btn
              icon
              small
              :title="$t('crudUnidades.form.fields.disassociateLines')"
              :loading="saving"
              @click.stop="requestDesassociar(item)"
            >
              <v-icon small color="red">mdi-link-variant-off</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
        <v-list-item v-if="!loading && !linhasFiltradas.length">
          <v-list-item-content>
            <v-list-item-title class="text--secondary text-body-2">
              {{ emptyListMessage }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-dialog v-model="confirmDialog" persistent max-width="500">
        <v-card>
          <v-card-title class="headline">
            <span style="color: #d60000">{{ $t('crudLinhas.messages.systemMessage') }}</span>
          </v-card-title>
          <v-card-text>
            <span style="color: #d60000">{{ confirmMessage }}</span>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="red accent-4" class="white--text" @click="cancelDesassociar">
              {{ $t('button.dismiss') }}
            </v-btn>
            <v-btn color="red accent-4" class="white--text" @click="confirmDesassociar">
              {{ $t('button.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import MultiplaEscolha from "@/components/Selects/MultiplaEscolha.vue";

export default {
  name: "UnidadeLinhasTable",
  components: {
    MultiplaEscolha,
  },
  props: {
    unidade: { type: Object, default: null },
    token: { type: String, default: null },
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      linhas: [],
      searchLinhas: "",
      loading: false,
      saving: false,
      linhasParaAssociar: [],
      confirmDialog: false,
      linhaPendente: null,
      requestSeq: 0,
    };
  },
  computed: {
    linhasFiltradas() {
      const list = this.linhas || [];
      const q = (this.searchLinhas || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((l) => {
        const fields = [l.nome_linha, l.cod_linha, l.id_linha];
        return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
      });
    },
    emptyListMessage() {
      if ((this.linhas || []).length && (this.searchLinhas || "").trim()) {
        return this.$t("crudUnidades.lines.emptySearch");
      }
      return this.$t("crudUnidades.lines.empty");
    },
    confirmMessage() {
      const linha = this.linhaPendente || {};
      const unidade = this.unidade || {};
      return this.$t("crudUnidades.lines.confirmDisassociate", {
        line: linha.cod_linha || linha.nome_linha || linha.id_linha || "-",
        unit: unidade.cod_unidade || unidade.descricao_unidade || unidade.id_unidade || "-",
      });
    },
  },
  watch: {
    unidade: {
      immediate: true,
      handler() {
        this.searchLinhas = "";
        this.linhasParaAssociar = [];
        this.resetAssociarSelect();
        this.load();
      },
    },
  },
  methods: {
    /** Rola até o seletor de associação (controle de mapa "Associar/desassociar linhas"). */
    focusAssociate() {
      this.$nextTick(() => {
        const row = this.$refs.associarRow;
        const el = row && row.$el ? row.$el : row;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        const select = this.$refs.linhasAssociarSelect;
        const input = select && select.$el ? select.$el.querySelector("input") : null;
        if (input && typeof input.focus === "function") input.focus();
      });
    },
    resetAssociarSelect() {
      const select = this.$refs.linhasAssociarSelect;
      if (select && typeof select.resetData === "function") select.resetData();
    },
    async load() {
      const id = this.unidade && this.unidade.id_unidade;
      if (id == null) {
        this.linhas = [];
        return;
      }
      const seq = ++this.requestSeq;
      this.loading = true;
      try {
        const res = await apiController.request(apiController.endpoints.getLinhasassociadas, {
          data: [id],
          silent: true,
        });
        if (seq !== this.requestSeq) return;
        this.linhas = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.linhas = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    async associarLinhas() {
      const idUnidade = this.unidade && this.unidade.id_unidade;
      if (idUnidade == null || !this.canEdit) return;
      const jaAssociadas = new Set(
        (this.linhas || [])
          .map((l) => Number(l.id_linha))
          .filter((id) => Number.isFinite(id))
      );
      const rows = (this.linhasParaAssociar || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && !jaAssociadas.has(id))
        .map((id) => [idUnidade, id]);
      if (!rows.length) {
        this.linhasParaAssociar = [];
        this.resetAssociarSelect();
        return;
      }
      this.saving = true;
      try {
        const res = await apiController.request(apiController.endpoints.associaLinhasbulk, {
          data: rows,
          silent: true,
        });
        if (res && res.data && res.data.erro) {
          throw new Error(res.data.mensagem || "associaLinhasbulk");
        }
        this.linhasParaAssociar = [];
        this.resetAssociarSelect();
        await this.load();
        this.$emit("changed");
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudUnidades.lines.associated"),
          tipo: "success",
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.saving = false;
      }
    },
    requestDesassociar(item) {
      if (!item) return;
      this.linhaPendente = { ...item };
      this.confirmDialog = true;
    },
    cancelDesassociar() {
      this.confirmDialog = false;
      this.linhaPendente = null;
    },
    async confirmDesassociar() {
      const item = this.linhaPendente;
      const idUnidade = this.unidade && this.unidade.id_unidade;
      this.confirmDialog = false;
      this.linhaPendente = null;
      if (!item || idUnidade == null) return;
      this.saving = true;
      try {
        // Payload em objeto: o backend escopa o DELETE pela unidade.
        const res = await apiController.request(apiController.endpoints.desassociaLinhasbulk, {
          data: { id_unidade: idUnidade, linhas: [item.id_linha] },
          silent: true,
        });
        if (res && res.data && res.data.erro) {
          throw new Error(res.data.mensagem || "desassociaLinhasbulk");
        }
        await this.load();
        this.$emit("changed");
        EventBus.$emit("show-alert", {
          mensagem: this.$t("crudUnidades.lines.disassociated"),
          tipo: "success",
        });
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.unidade-linhas-table__list {
  overflow-y: auto;
  min-height: 0;
}
</style>
